import React, { useContext, useEffect, useState } from "react";
import { useFetchApi } from "../../hooks/useFetchApi";
import Chart from "react-apexcharts";
import { GRAPH_TYPES } from "../../store/types";
import {
  captialCase,
  convertToInternationalCurrencySystem,
} from "../../helper";
import H5 from "../../components/Typography/H5";
import { StateContext } from "../../store/context";
import { isAllowed } from "../../components/Entitlements";
import { KEYS } from "../../setup/entitlements";
import Comparison from "../../components/GraphTypes/Comparison";
import { mergeAreaChartOptions, mergeChartOptions } from "./chartTheme";

interface Props {
  name?: string;
  snapshots: any;
  primaryStock: any;
  selectedStocks: any;
  staticGraphs?: any;
  compareResults: any;
  compareOtherStockResults: any;
}

const DynamicTab: React.FC<Props> = ({
  name,
  snapshots,
  primaryStock,
  selectedStocks,
  staticGraphs,
  compareResults,
  compareOtherStockResults,
}) => {
  const {
    execute: generateGraphs,
    loading: graphLoading,
    response: graphData,
  } = useFetchApi("/api/dynamic-graph");
  const [chartData, setChartData] = useState<any>([]);
  const {
    baseCurrency,
    authUser: { allowed },
  } = useContext(StateContext);
  // console.log('snapshots', snapshots);
  useEffect(() => {
    setChartData([]);
    if (
      snapshots.length > 0 &&
      (selectedStocks.length > 0 || primaryStock.length > 0)
    ) {
      setChartData((prev: any) => []);
      snapshots.forEach(async (snapshot: any) => {
        if (snapshot.graph_data && isAllowed([KEYS.DYNAMIC_GRAPHS], allowed)) {
          const parsedSnapshot = JSON.parse(snapshot.graph_data);
          const { selectedMetrics, graphType, period } = parsedSnapshot;

          generateGraphs({
            selectedMetrics,
            selectedStocks:
              name === "Comparable Stocks"
                ? [...primaryStock, ...selectedStocks]
                : primaryStock,
            filter: graphType,
            period,
            order_no: snapshot.order_no,
            categoryName: name,
            baseCurrency,
          });
        } else {
          setChartData((prev: any) =>
            [
              ...prev,
              ...(staticGraphs && staticGraphs[snapshot.graph_name]
                ? [
                    {
                      Component: staticGraphs[snapshot.graph_name],
                      order_no: snapshot.order_no,
                    },
                  ]
                : []),
            ].sort((a: any, b: any) => a.order_no - b.order_no)
          );
        }
      });
    }
  }, [selectedStocks]);

  useEffect(() => {
    if (!graphLoading && graphData) {
      if (
        !chartData.some((chart: any) => chart.order_no === graphData.order_no)
      ) {
        const c = [...chartData, graphData];
        setChartData(c.sort((a: any, b: any) => a.order_no - b.order_no));
      }
    }
  }, [graphData, graphLoading]);

  return (
    <div className="flex flex-col w-full h-full">
      {chartData && chartData.length ? (
        <>
          {chartData.map((chart: any) => {
            if (!chart.series) {
              const { Component } = chart;
              return (
                <Component
                  compareResults={compareResults}
                  compareOtherStockResults={compareOtherStockResults}
                />
              );
            }

            if (
              name === "Comparable Stocks" &&
              chart.categories &&
              chart.categories.length > 0
            ) {
              return (
                <Comparison
                  data={{
                    series: chart.series,
                    categories: chart.categories,
                    name: chart.name,
                  }}
                />
              );
            }

            const isAreaMetric =
              name === "Profitability" &&
              chart.series[0].metricGraphType === GRAPH_TYPES.PERCENTAGES;
            const mergeOptions = isAreaMetric
              ? mergeAreaChartOptions
              : mergeChartOptions;

            return (
              <div className="p-4 w-full vi-glass-card mt-8">
                <H5 color="text-[color:var(--vi-text)]">{captialCase(chart.name)}</H5>
                <Chart
                  options={mergeOptions({
                    chart: {
                      type: "line",
                      // one options object gets built per chart in this
                      // loop — give each its own id or ApexCharts treats
                      // them as the same instance
                      id: `dynamic-graph-${chart.order_no ?? chart.name}`,
                    },
                    tooltip: {
                      shared: true,
                      hideEmptySeries: false,
                      y: {
                        formatter: (value: any) => {
                          if (value === null || value === undefined || isNaN(Number(value))) {
                            return "N/A";
                          }
                          const metricGraphType =
                            chart.series[0].metricGraphType;
                          switch (metricGraphType) {
                            case GRAPH_TYPES.PERCENTAGES:
                              return `${Number(value).toFixed(0)}%`;
                            case GRAPH_TYPES.ABSOLUTE_VALUES:
                              return convertToInternationalCurrencySystem(
                                value
                              );
                            case GRAPH_TYPES.DAYS:
                              return `${Number(value).toFixed(0)} days`;
                            case GRAPH_TYPES.MULTIPLES:
                              return `${value}x`;
                            default:
                              return value;
                          }
                        },
                      },
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: (value: any) => {
                        // Hide label entirely for null/undefined/NaN values
                        if (value === null || value === undefined || isNaN(Number(value))) {
                          return "";
                        }
                        const metricGraphType =
                          chart.series[0].metricGraphType;
                        switch (metricGraphType) {
                          case GRAPH_TYPES.PERCENTAGES:
                            return `${Number(value).toFixed(0)}%`;
                          case GRAPH_TYPES.ABSOLUTE_VALUES:
                            return convertToInternationalCurrencySystem(
                              value
                            );
                          case GRAPH_TYPES.DAYS:
                            return `${Number(value).toFixed(0)} days`;
                          case GRAPH_TYPES.MULTIPLES:
                            return `${value}x`;
                          default:
                            return value;
                        }
                      },
                      offsetY: -10,
                    },
                    yaxis: {
                      labels: {
                        formatter: (value: any) => {
                          if (value === null || value === undefined || isNaN(Number(value))) {
                            return "";
                          }
                          const metricGraphType =
                            chart.series[0].metricGraphType;
                          switch (metricGraphType) {
                            case GRAPH_TYPES.PERCENTAGES:
                              return `${Number(value).toFixed(0)}%`;
                            case GRAPH_TYPES.ABSOLUTE_VALUES:
                              return `${baseCurrency} ${convertToInternationalCurrencySystem(
                                value
                              )}`;
                            case GRAPH_TYPES.DAYS:
                              return `${Number(value).toFixed(0)} days`;
                            case GRAPH_TYPES.MULTIPLES:
                              return `${Number(value).toFixed(2)}x`;
                            default:
                              return value;
                          }
                        },
                      },
                    },
                    xaxis: {
                      categories: chart.categories,
                    },
                  })}
                  series={
                    isAreaMetric
                      ? chart.series.map((s: any) => ({ ...s, type: "area" }))
                      : chart.series
                  }
                />
              </div>
            );
          })}
        </>
      ) : (
        <div className="flex justify-center items-center h-64">
          <h1 className="text-2xl">No Data Found</h1>
        </div>
      )}
    </div>
  );
};

export default React.memo(DynamicTab);
